import { useEffect, useRef, useState } from "react";
import type {
  Player,
  BracketConfig,
  StartGGTournamentStructure,
  StartGGEventSummary,
  StartGGPhase,
  SetupDraft,
} from "../types";
import {
  Container,
  Row,
  Col,
  Form,
  InputGroup,
  Button,
  Card,
  ToggleButton,
  ToggleButtonGroup,
} from "react-bootstrap";
import { extractTournamentSlug } from "../startgg";

interface SetupScreenProps {
  onStart: (
    players: Player[],
    config: BracketConfig,
    draft: SetupDraft,
  ) => void;
  // Setup state from a previous bracket generation, restored on re-entry
  initialDraft?: SetupDraft | null;
}

const DEFAULT_PLAYER_TEXT =
  "Alice\nBob\nCharlie\nDave\nEve\nFrank\nGrace\nHeidi";

/**
 * Pre-tournament setup screen. Lets the user enter a player list (one per line,
 * in seed order), configure round count, best-of format, and top-cut size,
 * then kicks off bracket generation.
 */
export default function SetupScreen({
  onStart,
  initialDraft,
}: SetupScreenProps) {
  const [playerText, setPlayerText] = useState(
    initialDraft?.playerText ?? DEFAULT_PLAYER_TEXT,
  );
  const [numRounds, setNumRounds] = useState(initialDraft?.numRounds ?? 3);
  const [bestOf, setBestOf] = useState<3 | 5>(initialDraft?.bestOf ?? 3);
  const [advancementCount, setAdvancementCount] = useState(
    initialDraft?.advancementCount ?? 4,
  );
  const [slug, setSlug] = useState(initialDraft?.slug ?? "");
  const [loadingTournament, setLoadingTournament] = useState(false);
  const [loadingEntrants, setLoadingEntrants] = useState(false);
  const [structure, setStructure] = useState<StartGGTournamentStructure | null>(
    initialDraft?.structure ?? null,
  );
  const [selectedEventId, setSelectedEventId] = useState<number | null>(
    initialDraft?.selectedEventId ?? null,
  );
  const [selectedPhaseId, setSelectedPhaseId] = useState<number | null>(
    initialDraft?.selectedPhaseId ?? null,
  );
  const [selectedPoolId, setSelectedPoolId] = useState<number | null>(
    initialDraft?.selectedPoolId ?? null,
  );

  const selectedEvent =
    structure?.events.find((e) => e.id === selectedEventId) ?? null;
  const selectedPhase =
    selectedEvent?.phases.find((p) => p.id === selectedPhaseId) ?? null;

  // Derive player names from the textarea, trimming whitespace and dropping blank lines
  const playerNames = playerText
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

  // Swiss convention: ceil(log2(n)) rounds, clamped to the available toggles (3–6).
  // Skipped on the first pass when a draft is restored, so a hand-picked round
  // count survives a trip through the bracket and back.
  const skipRoundAutoFill = useRef(initialDraft != null);
  useEffect(() => {
    if (skipRoundAutoFill.current) {
      skipRoundAutoFill.current = false;
      return;
    }
    if (playerNames.length < 2) return;
    const ideal = Math.ceil(Math.log2(playerNames.length));
    setNumRounds(Math.min(6, Math.max(3, ideal)));
  }, [playerNames.length]);

  // Convert the raw name list into Player objects (seeded by line order) and start the bracket
  const handleStart = () => {
    const players: Player[] = playerNames.map((name, i) => ({
      id: i + 1,
      name,
      seed: i + 1,
      dropped: false,
    }));
    onStart(
      players,
      { numRounds, bestOf, topCutCount: advancementCount },
      {
        slug,
        structure,
        selectedEventId,
        selectedPhaseId,
        selectedPoolId,
        playerText,
        numRounds,
        bestOf,
        advancementCount,
      },
    );
  };

  // Auto-select a phase's pool when there's only one to choose from
  const selectPhase = (phase: StartGGPhase) => {
    setSelectedPhaseId(phase.id);
    const onlyPool = phase.pools.length === 1 ? phase.pools[0] : undefined;
    setSelectedPoolId(onlyPool?.id ?? null);
  };

  // Auto-select an event's phase when there's only one to choose from
  const selectEvent = (event: StartGGEventSummary) => {
    setSelectedEventId(event.id);
    setSelectedPhaseId(null);
    setSelectedPoolId(null);
    const onlyPhase = event.phases.length === 1 ? event.phases[0] : undefined;
    if (onlyPhase) selectPhase(onlyPhase);
  };

  // Fetch a tournament's event/phase/pool structure from Start.GG
  const loadTournament = async () => {
    const resolvedSlug = extractTournamentSlug(slug);
    if (!resolvedSlug) return;
    setLoadingTournament(true);
    setStructure(null);
    setSelectedEventId(null);
    setSelectedPhaseId(null);
    setSelectedPoolId(null);
    try {
      const url = `/api/tournament/${encodeURIComponent(resolvedSlug)}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok) {
        alert(data.error ?? "Failed to load tournament");
        return;
      }
      const struct = data as StartGGTournamentStructure;
      setStructure(struct);
      const onlyEvent =
        struct.events.length === 1 ? struct.events[0] : undefined;
      if (onlyEvent) selectEvent(onlyEvent);
    } catch (err) {
      alert("Failed to fetch from Start.GG");
    } finally {
      setLoadingTournament(false);
    }
  };

  // Clear the loaded tournament and restore the slug/URL input for a new search
  const resetTournament = () => {
    setStructure(null);
    setSelectedEventId(null);
    setSelectedPhaseId(null);
    setSelectedPoolId(null);
    setSlug("");
  };

  // Fetch the selected pool's entrants and populate the player textarea sorted by seed
  const importPoolEntrants = async () => {
    if (selectedPoolId == null) return;
    setLoadingEntrants(true);
    try {
      const res = await fetch(`/api/pool-entrants/${selectedPoolId}`);
      const data = await res.json();
      if (!res.ok) {
        alert(data.error ?? "Failed to load entrants");
        return;
      }
      const names = (data.players as Player[])
        .sort((a, b) => a.seed - b.seed)
        .map((p) => p.name);
      setPlayerText(names.join("\n"));
    } catch (err) {
      alert("Failed to fetch entrants from Start.GG");
    } finally {
      setLoadingEntrants(false);
    }
  };

  const buttons = [];

  {
    for (var i = 3; i < 7; i++) {
      buttons.push(
        <ToggleButton
          key={i}
          id={`${i}rounds`}
          value={i}
          variant="outline-primary"
        >
          {i}
        </ToggleButton>,
      );
    }
  }

  return (
    <Container className="pb-5">
      <Row className="g-3">
        <Col md={7}>
          <Card>
            <Card.Header className="fw-semibold">Players</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted mb-1">
                  Import from Start.GG
                </Form.Label>
                {!structure ? (
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Tournament URL or slug"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && loadTournament()}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={loadTournament}
                      disabled={loadingTournament || !slug.trim()}
                    >
                      {loadingTournament ? "Loading…" : "Load Tournament"}
                    </Button>
                  </InputGroup>
                ) : (
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="small text-muted">
                      Current Tournament:{" "}
                      <strong>{structure.tournament.name}</strong>
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={resetTournament}
                    >
                      Change
                    </Button>
                  </div>
                )}

                {structure && (
                  <div className="mt-2 d-flex flex-column gap-2">
                    {structure.events.length > 1 && (
                      <Form.Select
                        size="sm"
                        value={selectedEventId ?? ""}
                        onChange={(e) => {
                          const event = structure.events.find(
                            (ev) => ev.id === Number(e.target.value),
                          );
                          if (event) selectEvent(event);
                        }}
                      >
                        <option value="" disabled>
                          Select event…
                        </option>
                        {structure.events.map((ev) => (
                          <option key={ev.id} value={ev.id}>
                            {ev.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}

                    {selectedEvent && selectedEvent.phases.length > 1 && (
                      <Form.Select
                        size="sm"
                        value={selectedPhaseId ?? ""}
                        onChange={(e) => {
                          const phase = selectedEvent.phases.find(
                            (p) => p.id === Number(e.target.value),
                          );
                          if (phase) selectPhase(phase);
                        }}
                      >
                        <option value="" disabled>
                          Select phase…
                        </option>
                        {selectedEvent.phases.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}

                    {selectedPhase && selectedPhase.pools.length > 1 && (
                      <Form.Select
                        size="sm"
                        value={selectedPoolId ?? ""}
                        onChange={(e) =>
                          setSelectedPoolId(Number(e.target.value))
                        }
                      >
                        <option value="" disabled>
                          Select pool…
                        </option>
                        {selectedPhase.pools.map((pool) => (
                          <option key={pool.id} value={pool.id}>
                            {pool.displayIdentifier}
                          </option>
                        ))}
                      </Form.Select>
                    )}

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={importPoolEntrants}
                      disabled={loadingEntrants || selectedPoolId == null}
                    >
                      {loadingEntrants ? "Importing…" : "Import Players"}
                    </Button>
                  </div>
                )}
              </Form.Group>

              <Form.Group>
                <div className="d-flex justify-content-between align-items-baseline mb-1">
                  <Form.Label className="small text-muted mb-0">
                    Players — one per line, in seed order
                  </Form.Label>
                  <span className="small text-muted">
                    {playerNames.length} entered
                  </span>
                </div>
                <Form.Control
                  as="textarea"
                  value={playerText}
                  onChange={(e) => setPlayerText(e.target.value)}
                  rows={12}
                  style={{ fontFamily: "var(--bs-font-monospace)" }}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        <Col md={5}>
          <Card className="mb-3">
            <Card.Header className="fw-semibold">Format</Card.Header>
            <Card.Body className="d-flex flex-column gap-3">
              <div>
                <Form.Label className="small text-muted mb-1">
                  Rounds
                </Form.Label>
                <ToggleButtonGroup
                  type="radio"
                  name="numRounds"
                  value={numRounds}
                  onChange={(val: number) => setNumRounds(val)}
                  className="d-flex"
                >
                  {buttons}
                </ToggleButtonGroup>
              </div>

              <div>
                <Form.Label className="small text-muted mb-1">
                  Match length
                </Form.Label>
                <ToggleButtonGroup
                  type="radio"
                  name="bestOf"
                  value={bestOf}
                  onChange={(val: number) => setBestOf(val as 3 | 5)}
                  className="d-flex"
                >
                  <ToggleButton id="bo3" value={3} variant="outline-primary">
                    Best of 3
                  </ToggleButton>
                  <ToggleButton id="bo5" value={5} variant="outline-primary">
                    Best of 5
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              <div>
                <Form.Label className="small text-muted mb-1">
                  Players advancing to top cut
                </Form.Label>
                <Form.Control
                  type="number"
                  value={advancementCount}
                  min={1}
                  max={Math.max(1, playerNames.length)}
                  onChange={(e) => setAdvancementCount(Number(e.target.value))}
                />
              </div>
            </Card.Body>
          </Card>

          <Button
            variant="primary"
            size="lg"
            className="w-100"
            disabled={playerNames.length < 2}
            onClick={handleStart}
          >
            {playerNames.length < 2
              ? "Add at least 2 players"
              : `Generate Bracket (${playerNames.length} players)`}
          </Button>
        </Col>
      </Row>
    </Container>
  );
}
